import React, { useState, useEffect, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { useAuth } from '../api/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, TextField, Checkbox, FormControlLabel, Switch } from '@mui/material';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { newExportObjectAsPDF } from "../util/NewExportPdf";

const appUrl = process.env.REACT_APP_APP_URL;

if (!appUrl) {
  throw new Error("REACT_APP_APP_URL is not defined. Please set it in your .env file.");
}

const PotentialProblematicView = () => {
  const { authState } = useAuth();
  const [problematicDwrs, setProblematicDwrs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDwr, setSelectedDwr] = useState(null);
  const token = authState?.token;
  const navigate = useNavigate();
  const [includeReviewed, setIncludeReviewed] = useState(false);

  // Format date for input fields
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Set date range for fetching DWRs (default to last 30 days)
  const [startDate, setStartDate] = useState(formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30))));
  const [endDate, setEndDate] = useState(formatDateForInput(new Date()));
  const [dateError, setDateError] = useState('');

  // Date validation function
  const validateDateRange = (start, end) => {
    const today = new Date().setHours(0, 0, 0, 0);
    const startDate = new Date(start).setHours(0, 0, 0, 0);
    const endDate = new Date(end).setHours(0, 0, 0, 0);
    
    if (startDate > today) {
      setDateError("Start date cannot be in the future");
      return false;
    }
    
    if (endDate > today) {
      setDateError("End date cannot be in the future");
      return false;
    }

    if (startDate > endDate) {
      setDateError("Start date must be before end date");
      return false;
    }

    setDateError('');
    return true;
  };

  // Fetch DWRs with post-approval changes
  const fetchProblematicDWRs = useCallback(async () => {
    if (!validateDateRange(startDate, endDate)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${appUrl}/jmrapp/dailyworkrecords/problematic/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startDate,
          end_date: endDate,
          include_reviewed: includeReviewed
        }
      });
      setProblematicDwrs(response.data);
    } catch (error) {
      console.error("Failed to fetch problematic DWRs:", error);
      confirmAlert({
        title: "Error",
        message: "Failed to load problematic DWRs. Please try again.",
        buttons: [{ label: "OK" }],
      });
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, token, includeReviewed]);

  useEffect(() => {
    if (authState.authenticated) {
      fetchProblematicDWRs();
    }
  }, [authState.authenticated, fetchProblematicDWRs]);

  const handleDwrClick = async (dwr) => {
    try {
      const token = localStorage.getItem('my-jwt');
      setIsLoading(true);
      
      // Fetch the full DWR data
      const dwrResponse = await axios.get(`${appUrl}/jmrapp/dailyworkrecords/${dwr.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const wellId = dwrResponse.data.well.id;
      const wellResponse = await axios.get(`${appUrl}/jmrapp/wells/${wellId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const subprojectId = dwrResponse.data.subproject?.id;
      const subprojectType = dwrResponse.data.subproject?.subproject_type;
      
      // Navigate to the Well component with state set to automatically show the specific DWR
      navigate('/well', { 
        state: { 
          well: wellResponse.data,
          showDWR: true,  // This will make the Well component show the DWR immediately
          activeTab: 'projects', // Set the active tab to 'projects'
          selectedSubproject: { // Provide the needed subproject info
            id: subprojectId,
            subproject_type: subprojectType
          },
          selectedDwrId: dwr.id, // Add the specific DWR ID to view
          fromProblematicView: true // Flag to indicate we're coming from the problematic view
        }
      });
      
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to fetch full DWR data:', error);
      confirmAlert({
        title: "Error",
        message: "Failed to load DWR data. Please try again.",
        buttons: [{ label: "OK" }],
      });
    }
  };

  const handlePDFDownload = async (dwr, event) => {
    event.stopPropagation();
    try {
      const fullDwrData = await fetchFullDWRData(dwr.id);
      await newExportObjectAsPDF(fullDwrData, token);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      confirmAlert({
        title: "Error",
        message: "Failed to generate PDF. Please try again.",
        buttons: [{ label: "OK" }],
      });
    }
  };

  const fetchFullDWRData = async (dwrId) => {
    try {
      const response = await axios.get(`${appUrl}/jmrapp/dailyworkrecords/${dwrId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch full DWR data for ID ${dwrId}:`, error);
      throw error;
    }
  };

  const handleMarkAsReviewed = async (dwr, event) => {
    event.stopPropagation();
    try {
      // Mark as reviewed
      await axios.post(`${appUrl}/jmrapp/dailyworkrecords/${dwr.id}/review/`, 
        { is_reviewed: !dwr.is_reviewed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh the data
      fetchProblematicDWRs();
      
      confirmAlert({
        title: "Success",
        message: `DWR has been marked as ${dwr.is_reviewed ? 'not reviewed' : 'reviewed'}.`,
        buttons: [{ label: "OK" }],
      });
    } catch (error) {
      console.error('Failed to update review status:', error);
      confirmAlert({
        title: "Error",
        message: "Failed to update review status. Please try again.",
        buttons: [{ label: "OK" }],
      });
    }
  };

  // Columns for the DataGrid
  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'date', headerName: 'Date', width: 120 },
    { field: 'well_name', headerName: 'Well', width: 200 },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'change_type', headerName: 'Change Type', width: 150,
      valueGetter: (params) => {
        if (params.row.changes_after_approval) return 'After Approval';
        if (params.row.changes_after_lock) return 'After Lock';
        if (params.row.changes_after_sales_order) return 'After Sales Order';
        return 'Unknown';
      }
    },
    { field: 'last_modified', headerName: 'Last Modified', width: 180 },
    { 
      field: 'is_reviewed', 
      headerName: 'Reviewed', 
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.row.is_reviewed ? (
            <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
              Yes
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">No</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 300,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={(e) => handlePDFDownload(params.row, e)}
            startIcon={<FileDownloadIcon />}
            size="small"
          >
            PDF
          </Button>
          <Button 
            onClick={(e) => handleMarkAsReviewed(params.row, e)}
            variant={params.row.is_reviewed ? "outlined" : "contained"}
            color={params.row.is_reviewed ? "error" : "success"}
            size="small"
            sx={{ 
              minWidth: '150px',
              px: 2
            }}
          >
            {params.row.is_reviewed ? 'Unmark' : 'Mark Reviewed'}
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <div className="problematic-dwr-container">
      <Typography variant="h6" gutterBottom>
        Potentially Problematic DWRs
      </Typography>
      
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <FormControlLabel
          control={
            <Switch
              checked={includeReviewed}
              onChange={(e) => setIncludeReviewed(e.target.checked)}
              color="primary"
            />
          }
          label="Include Reviewed"
        />
        <Button 
          variant="contained" 
          onClick={fetchProblematicDWRs}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Search'}
        </Button>
      </Box>
      
      {dateError && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {dateError}
        </Typography>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={problematicDwrs}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          onRowClick={(params) => handleDwrClick(params.row)}
          loading={isLoading}
          disableSelectionOnClick
        />
      </Box>
    </div>
  );
};

export default PotentialProblematicView; 
